import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import moment from 'moment';

import { APP_TITLE } from '@utils';
import { useSession } from '@hooks';
import {
  useGetConsumerMetaQuery,
  useGetSpotMetaQuery,
  useGetEventMetaQuery,
  useGetBookingMetaQuery,
} from '@graphql';

function getSiteOrigin(): string {
  if (typeof window === 'undefined') return 'https://festayre.com';
  return window.location.origin;
}

function buildCanonical(origin: string, pathname: string): string {
  const cleanPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return `${origin}${cleanPath}`;
}

export function Meta() {
  const { account } = useSession();
  const [title, setTitle] = React.useState<string | undefined>(APP_TITLE);
  const [description, setDescription] = React.useState<string | undefined>('Events • Spots');
  const [image, setImage] = React.useState<string | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const params = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  const locale = `${i18n.language}_${i18n.language.toUpperCase()}`;

  const { entity, variables } = React.useMemo(() => {
    const result = { entity: '', variables: {} };

    const consumerSP = searchParams.get('c');
    const eventSP = searchParams.get('e');
    const spotSP = searchParams.get('s');

    if (consumerSP || eventSP || spotSP) {
      if (consumerSP) {
        return { entity: 'consumer', variables: { slug: consumerSP } };
      } else if (eventSP) {
        return { entity: 'event', variables: { id: eventSP } };
      } else if (spotSP) {
        return { entity: 'spot', variables: { slug: spotSP } };
      }
    }

    const { eventId, bookingId, slug } = params;

    if (eventId) {
      return { entity: 'event', variables: { id: eventId } };
    } else if (slug) {
      return { entity: 'spot', variables: { slug } };
    } else if (bookingId) {
      return { entity: 'booking', variables: { id: bookingId } };
    }

    return result;
  }, [searchParams, params]);

  const { data: consumerData } = useGetConsumerMetaQuery({
    skip: entity !== 'consumer',
    // @ts-ignore
    variables,
    fetchPolicy: 'cache-first',
  });

  const { data: spotData } = useGetSpotMetaQuery({
    skip: entity !== 'spot',
    // @ts-ignore
    variables,
    fetchPolicy: 'cache-first',
  });

  const { data: eventData } = useGetEventMetaQuery({
    skip: entity !== 'event',
    // @ts-ignore
    variables,
    fetchPolicy: 'cache-first',
  });

  const { data: bookingData } = useGetBookingMetaQuery({
    skip: entity !== 'booking',
    // @ts-ignore
    variables,
    fetchPolicy: 'cache-first',
  });

  const origin = getSiteOrigin();
  const canonicalUrl = buildCanonical(origin, location.pathname);

  React.useEffect(() => {
    const { pathname } = location;

    let title = APP_TITLE;
    let description = 'Events • Spots';
    let image: string | undefined = undefined;

    if (pathname === '/spots') {
      // HOME SPOTS
      title = `Spots | ${APP_TITLE}`;
      description = 'Spots ' + t('word.to discover');
    } else if (pathname === '/events') {
      // HOME EVENTS
      title = `Events | ${APP_TITLE}`;
      description = 'Events ' + t('word.to discover');
    } else if (pathname === '/search') {
      // HOME SEARCH
      const search = searchParams.get('search');
      title = `${t('word.Search')} ${search ?? ''} | ${APP_TITLE}`;
      description = (search ?? '') + ' ' + t('word.to discover');
    } else if (pathname.startsWith('/me/calendar') || pathname.startsWith('/me/table')) {
      const date = searchParams.get('date') ?? moment().format('YYYY-MM-DD');
      title = `${t('word.My agenda')} ${moment(date).format('LL')} | ${APP_TITLE}`;
    } else if (pathname === '/me/dashboard/preview') {
      title = `${t('word.Dashboard')} | ${APP_TITLE}`;
      description = t('openGraph.dashboard.description.spot');
    }

    setTitle(title);
    setDescription(description);
    setImage(image);
  }, [location.pathname]);

  React.useEffect(() => {
    let title = APP_TITLE;
    let description = 'Events • Spots';
    let image: string | undefined = undefined;

    if (bookingData?.booking) {
      const { booking } = bookingData;
      const date = moment(booking.startTime).format('LLL');
      let computedTitle = 'Booking';

      if (account?.__typename === 'Consumer') {
        computedTitle = t(`meta title.consumer`, { ns: 'booking', date, spot: booking.spot.name });
      } else if (account?.__typename === 'Spot') {
        let firstname = '';
        if (booking.client.__typename === 'BookingClient') {
          firstname = booking.client.firstname;
        } else if (booking.client.__typename === 'Consumer') {
          firstname = booking.client.user.firstname;
        }
        computedTitle = t(`meta title.spot`, { ns: 'booking', date, firstname });
      }

      title = `${computedTitle} | ${APP_TITLE}`;
      description = 'Booking';
      image = undefined;
    }

    if (consumerData?.consumer) {
      const { consumer } = consumerData;
      title = `${consumer.name} | ${APP_TITLE}`;
      description = consumer.description || t('word.to discover');
      image = consumer.avatar?.url;
    }

    if (spotData?.spot) {
      const { spot } = spotData;
      title = `${spot.name} | Spot | ${APP_TITLE}`;
      description = 'Spot ' + t('word.to discover');
      image = spot.covers[0]?.url;
    }

    if (eventData?.event) {
      const { event } = eventData;
      title = `${event.title} | Event | ${APP_TITLE}`;
      description = 'Event ' + t('word.to discover');
      image = event.covers[0]?.url;
    }

    setTitle(title);
    setDescription(description);
    setImage(image);
  }, [spotData, consumerData, eventData, bookingData]);

  return (
    <Helmet>
      <title>{title}</title>

      <meta name="robots" content="index,follow" />
      <link rel="canonical" href={canonicalUrl} />

      {/* BASE OPEN GRAPH */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={locale} />

      {/* TWITTER OPEN GRAPH */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:url" content={canonicalUrl} />
    </Helmet>
  );
}
